/**
 * The form's way back to the document (#169 slice 3): each change is sent
 * to the backend's render, `RENDER_DEBOUNCE_MS` after the last one, and its
 * YAML replaces the text. Render is pure (no GitLab call). Only the newest
 * change's answer is kept, so a slow older one never overwrites it.
 *
 * `error` is why render refused the form (an incomplete definition, e.g.
 * no pattern yet): the text then stays the last complete definition.
 */

import { useEffect, useRef, useState } from 'react';
import { renderDefinition } from '../api/projectPanel';
import { definitionFrom, type DefinitionFields } from '../utils/definitionForm';
import { renderErrorMessage } from '../utils/errors';

export const RENDER_DEBOUNCE_MS = 250;

export interface FormRender {
  request: (fields: DefinitionFields) => void;
  /** A change not yet rendered: the text is behind the form. */
  pending: boolean;
  error: string | null;
}

export function useFormRender(projectId: string, onRendered: (content: string) => void): FormRender {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const latest = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const seq = latest;
    const pendingTimer = timer;
    return () => {
      if (pendingTimer.current !== null) clearTimeout(pendingTimer.current);
      seq.current += 1;
    };
  }, []);

  const request = (fields: DefinitionFields): void => {
    if (timer.current !== null) clearTimeout(timer.current);
    latest.current += 1;
    const mine = latest.current;
    setPending(true);
    timer.current = setTimeout(() => {
      timer.current = null;
      renderDefinition(projectId, definitionFrom(fields)).then(
        (content) => {
          if (mine !== latest.current) return;
          setPending(false);
          setError(null);
          onRendered(content);
        },
        (failure: unknown) => {
          if (mine !== latest.current) return;
          setPending(false);
          setError(renderErrorMessage(failure));
        },
      );
    }, RENDER_DEBOUNCE_MS);
  };

  return { request, pending, error };
}
